import { ButcherDetails, Notification } from '@helpers/Models';
import React, { useEffect, useRef, useState } from 'react';
import { Assets } from '@helpers/Constants/Assets';
import {
  faArrowRightFromBracket,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import { FrontendRoutes } from '@helpers/Constants/Navigation';
import { AuthProvider } from '@providers/AuthProvider';
import { NotificationPopupComponent } from './NotificationPopupComponent';
import { NotificationManager } from '@services/NotificationManager';
import { getUserInitial } from '@services/UserService';
import { useToast } from '@contexts/Toast';
// import { Layout } from './Layout/Layout';

export const ButcherHeader: React.FC<{ isOnBoarding?: boolean }> = ({
  isOnBoarding,
}) => {
  // Initialize state with a default empty object matching the expected structure
  const [butcherDetails, setButcherDetails] = useState<ButcherDetails | null>();
  const [open, setOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isNotificationPopupOpen, setIsNotificationPopupOpen] = useState(false);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  // create a state variable for notification
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const menuRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  // const [showLayout, setShowLayout] = useState(false);
  const [initials, setInitials] = useState<string>('');
  const { showToastMessage } = useToast();

  // Handle notification bell click
  const handleNotificationBellClick = () => {
    setIsNotificationPopupOpen(!isNotificationPopupOpen);
  };

  // Handle clicking outside to close notification popup and menu
  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node;

    // Close profile menu if click is outside
    if (menuRef.current && !menuRef.current.contains(target)) {
      setOpen(false);
    }

    // Close notification popup if click is outside
    if (notificationRef.current && !notificationRef.current.contains(target)) {
      setIsNotificationPopupOpen(false);
    }
  };

  useEffect(() => {
    const storedDetails = localStorage.getItem('butcherDetails');
    const isAdminFlag = localStorage.getItem('adminFlag');
    setIsAdmin(isAdminFlag === '1');

    if (storedDetails) {
      try {
        const parsedDetails: ButcherDetails = JSON.parse(storedDetails);
        setButcherDetails(parsedDetails);
        const contactName = parsedDetails?.butcher?.contactName;
        // Getting initials from the contact name
        if (contactName) {
          setInitials(getUserInitial(contactName));
        }
      } catch (error) {
        console.error('Error parsing butcherDetails from localStorage', error);
      }
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Use centralized NotificationManager for notification state management
  useEffect(() => {
    // Initial load of notifications
    const loadNotifications = () => {
      const notificationsWithReadStatus =
        NotificationManager.getNotificationsWithReadStatus();
      const unreadCount = NotificationManager.getUnreadCount();

      setNotifications(notificationsWithReadStatus);
      setUnreadNotificationsCount(unreadCount);
    };

    // Load initial notifications
    loadNotifications();

    // Subscribe to notification updates
    const unsubscribe = NotificationManager.subscribeToUpdates((data) => {
      console.log('ButcherHeader received notification update:', data);
      if (data && data.notifications) {
        setNotifications(data.notifications);
      }
      if (data && typeof data.unreadCount === 'number') {
        setUnreadNotificationsCount(data.unreadCount);
      }
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, []);
  /**
   * Logs out the current user by calling the `logout` method from the `AuthProvider`.
   * After logging out, it navigates the user to the login page.
   *
   * @async
   * @function logout
   * @returns {Promise<void>} A promise that resolves when the logout process is complete.
   */
  const logout = async () => {
    await AuthProvider.logout();
    navigate(FrontendRoutes.LOGIN);
  };

  /**
   * Retrieves the full address of the butcher store.
   *
   * @returns {string} The concatenated address string consisting of address lines 1, 2, and 3, city, and postal code.
   */
  const getButcherAddress = () => {
    // return `${butcherDetails.storeAddress?.address1}, ${butcherDetails.storeAddress?.address2}, ${butcherDetails.storeAddress?.address3}, ${butcherDetails.storeAddress?.city}, ${butcherDetails.storeAddress?.postalCode}`;
    // Showing empty space when there's no address entered
    return `${butcherDetails?.storeAddress ?? ''}`;
  };

  /**
   * Handles navigation to Profile Details with onboarding validation.
   * Shows a toast message if the first two onboarding steps (Logo and Address) are not completed.
   */
  const handleProfileDetailsClick = () => {
    setOpen(false);

    // Check if user is admin and has onboarding steps
    if (isAdmin) {
      const onboardingSteps = JSON.parse(
        localStorage.getItem('onboardingSteps') || 'null',
      );

      // If onboarding steps exist, check if first two steps are completed
      if (onboardingSteps && Array.isArray(onboardingSteps)) {
        const isLogoAndAddressCompleted =
          onboardingSteps[0]?.isComplete && onboardingSteps[1]?.isComplete;

        if (!isLogoAndAddressCompleted) {
          showToastMessage(
            'Please complete the store setup to access the Profile details',
            'error',
          );
          return;
        }
      }
    }

    // If validation passes or user is not admin, navigate to profile details
    navigate(FrontendRoutes.PROFILE_DETAILS, {
      state: isAdmin ? { isAdmin: true } : undefined,
    });
  };

  return (
    <div className="flex items-center w-full justify-between px-4">
      <div className="flex items-start">
        {isOnBoarding && (
          <img
            src={Assets.LOGO_ICON}
            alt="Butcher House"
            className="w-16 h-16 object-contain cursor-pointer mr-2"
          />
        )}
        <div>
          <div className="flex items-center">
            <img
              src={
                butcherDetails?.profileImage?.logoLocation ||
                `${Assets.BUTCHER_HOUSE}`
              }
              alt="Butcher House"
              className="w-8 h-8 object-contain cursor-pointer mr-2"
            />
            <p className="text-sm font-semibold text-gray-800">
              {butcherDetails?.butcher?.companyName}
            </p>
          </div>
          <p className="text-sm text-gray-500">{getButcherAddress()}</p>
        </div>
      </div>
      <div className="flex flex-row gap-x-2">
        {/* Notification bell - visible for all users when not onboarding */}
        {!isOnBoarding && (
          <div className="flex justify-center items-center relative">
            <button
              type="button"
              onClick={handleNotificationBellClick}
              className="w-[38px] h-[38px] cursor-pointer rounded-full border border-[#E6E6E6] relative hover:bg-gray-50 transition-colors flex items-center justify-center"
              aria-label="Notifications"
            >
              <svg
                width="38"
                height="24"
                viewBox="0 0 20 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.400391 20.4V18H2.80039V9.6C2.80039 7.94 3.30039 6.465 4.30039 5.175C5.30039 3.885 6.60039 3.04 8.20039 2.64V1.8C8.20039 1.3 8.37539 0.875 8.72539 0.525C9.07539 0.175 9.50039 0 10.0004 0C10.5004 0 10.9254 0.175 11.2754 0.525C11.6254 0.875 11.8004 1.3 11.8004 1.8V2.64C13.4004 3.04 14.7004 3.885 15.7004 5.175C16.7004 6.465 17.2004 7.94 17.2004 9.6V18H19.6004V20.4H0.400391ZM10.0004 24C9.34039 24 8.77539 23.765 8.30539 23.295C7.83539 22.825 7.60039 22.26 7.60039 21.6H12.4004C12.4004 22.26 12.1654 22.825 11.6954 23.295C11.2254 23.765 10.6604 24 10.0004 24ZM5.20039 18H14.8004V9.6C14.8004 8.28 14.3304 7.15 13.3904 6.21C12.4504 5.27 11.3204 4.8 10.0004 4.8C8.68039 4.8 7.55039 5.27 6.61039 6.21C5.67039 7.15 5.20039 8.28 5.20039 9.6V18Z"
                  fill="#004834"
                />
              </svg>
              {/* Unread notification indicator */}
              {unreadNotificationsCount > 0 && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full flex items-center justify-center">
                  <span
                    className="text-white text-xs font-bold"
                    style={{ fontSize: '8px' }}
                  >
                    {unreadNotificationsCount > 9
                      ? '9+'
                      : unreadNotificationsCount}
                  </span>
                </div>
              )}
            </button>

            {/* Notification Popup */}
            {isNotificationPopupOpen && (
              <div
                ref={notificationRef}
                className="absolute top-full right-0 mt-2 z-50"
              >
                <NotificationPopupComponent
                  notifications={notifications}
                  onClose={() => setIsNotificationPopupOpen(false)}
                />
              </div>
            )}
          </div>
        )}

        <div className="relative" ref={menuRef}>
          <button
            type="button"
            className="w-[38px] h-[38px] rounded-full overflow-hidden bg-primary  border border-gray-200 hover:ring-2 hover:ring-gray-300"
            onClick={() => setOpen((prev) => !prev)}
          >
            {/* Render initials */}
            <span className="text-white font-semibold">{initials}</span>
          </button>
          {/* Dropdown menu */}
          {open && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-50">
              <button
                type="button"
                className="flex items-center gap-3 px-4 py-2 hover:bg-gray-100 w-full font-semibold"
                onClick={handleProfileDetailsClick}
              >
                <FontAwesomeIcon icon={faUser} />
                Profile Details
              </button>

              <button
                type="button"
                className="flex items-center gap-3 px-4 py-2 text-primary hover:bg-gray-100 w-full font-semibold"
                onClick={logout}
              >
                <FontAwesomeIcon icon={faArrowRightFromBracket} />
                Log Out
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
